import { FormEvent, useEffect, useState } from "react";
import { CalendarCheck2, Images, MessageCircleOff, Search, Sparkles, Target, Zap } from "lucide-react";

import "./MasterLanding.css";
import feedScreen from "./assets/master-landing-feed.jpg";
import postScreen from "./assets/master-landing-post.png";
import profileScreen from "./assets/master-landing-profile.png";
import workImage from "./assets/master-landing-work.jpg";
// TODO: сохрани эти 4 файла в assets с такими именами (или поправь имена под свои)
import workImage2 from "./assets/image2.jpg";
import workImage3 from "./assets/image3.jpg";
import workImage4 from "./assets/image4.jpg";
import workImage5 from "./assets/image5.jpg";
import workImage6 from "./assets/image6.jpg";
import { MasterLandingService } from "./MasterLandingService";
import { useNavigate } from "react-router-dom";

// TODO: подставить актуальную ссылку на реальную ленту Looksty, если она отличается от корня домена
const FEED_URL = "https://looksty.ru";

const painCards = [
  {
    icon: Sparkles,
    text: "В портфолио — сложный дизайн и ламинирование. А спрашивают «а попроще есть, подешевле?»",
  },
  {
    icon: Images,
    text: "Лучшая работа месяца лежит в галерее телефона — и на этом всё.",
  },
  {
    icon: Search,
    text: "Сарафан работает. Но только среди тех, кто и так вас знает.",
  },
];

const steps = [
  {
    text: "Листает ленту работ мастеров Томска — без прайсов и звёздочек",
    image: feedScreen,
    alt: "Лента работ мастеров в Looksty",
  },
  {
    text: "Останавливается на той, что зацепила",
    image: postScreen,
    alt: "Карточка работы в Looksty",
  },
  {
    text: "Смотрит, кто автор, и записывается прямо там",
    image: profileScreen,
    alt: "Профиль мастера в Looksty",
  },
];

const benefits = [
  {
    icon: Target,
    title: "Ленту смотрят ради стиля",
    text: "Приходят не за самой низкой ценой, а за тем, что увидели на фото — и захотели так же.",
  },
  {
    icon: Zap,
    title: "Видно, что вы правда умеете",
    text: "Сложные техники и авторские работы — а не только базовые услуги для галочки.",
  },
  {
    icon: MessageCircleOff,
    title: "Меньше вопросов до записи",
    text: "Работа, цена и расписание — уже на экране.",
  },
  {
    icon: CalendarCheck2,
    title: "Всё в одном месте",
    text: "Портфолио, услуги и запись — на одной странице.",
  },
];

const feedThumbnails = [workImage3, workImage, workImage2, workImage4, workImage5];

export const MasterLandingPage = () => {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Looksty — покажите работы, а не объясняйте в директе";

    const revealElements = Array.from(document.querySelectorAll<HTMLElement>("[data-landing-reveal]"));

    if (typeof IntersectionObserver === "undefined") {
      revealElements.forEach((element) => element.classList.add("is-visible"));
      return () => {
        document.title = previousTitle;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -10%",
        threshold: 0.12,
      }
    );

    revealElements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
      document.title = previousTitle;
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedName = name.trim();
    const normalizedContact = contact.trim();

    if (!normalizedName || !normalizedContact) {
      setFormError("Заполните имя и контакт для связи");
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    setFormError("");

    try {
      const response = await MasterLandingService.SubmitLead({
        name: normalizedName,
        contact: normalizedContact,
        source: "master_landing",
      });

      if (!response.success) {
        throw new Error("lead submit failed");
      }

      navigate("/master-lending/success", {
        replace: true,
        state: {
          name: normalizedName,
        },
      });
    } catch {
      setFormError("Не удалось отправить заявку. Попробуйте ещё раз или напишите нам напрямую.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="ll-page">
      <header className="ll-header">
        <a className="ll-logo" href="#top">
          Looksty
        </a>
        <nav className="ll-nav" aria-label="Навигация лендинга">
          <a href="#how">Как это работает</a>
          <a href="#feed">Лента</a>
          <a href="#benefits">Что вы получите</a>
        </nav>
        <a className="ll-nav-cta" href="#master-landing-form">
          Показать работы
        </a>
      </header>

      <section className="ll-hero" id="top">
        <div className="ll-container">
          <div className="ll-hero-card" data-landing-reveal>
            <div className="ll-hero-card-copy">
              <span className="ll-eyebrow">Looksty — Томск</span>
              <h1>
                Выбирают не того, кто лучше. Выбирают того, кого <em>успели увидеть</em>.
              </h1>
              <p>Looksty — лента Томска, где клиентки листают реальные работы мастеров и записываются к тем, чей стиль зацепил. Не рейтинг, не сарафан — то, что вы правда умеете.</p>
              <div className="ll-hero-actions">
                <a className="ll-btn" href="#master-landing-form">
                  Показать свои работы
                </a>
                <a className="ll-link" href="#feed">
                  Посмотреть ленту →
                </a>
              </div>
            </div>
            <div className="ll-hero-card-visual">
              <img src={workImage} alt="Реальная работа мастера в Looksty" />
            </div>
          </div>
          <span className="ll-hero-note">Мастеров пока немного — тем заметнее каждая работа</span>
        </div>
      </section>

      <section className="ll-pain">
        <div className="ll-container">
          <h2 data-landing-reveal>Знакомо?</h2>

          <div className="ll-pain-grid">
            {painCards.map(({ icon: Icon, text }, index) => (
              <div className="ll-icon-card" key={index} data-landing-reveal>
                <span className="ll-icon-badge">
                  <Icon size={20} color="#fff" />
                </span>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ll-how" id="how">
        <div className="ll-container">
          <div className="ll-how-head" data-landing-reveal>
            <span className="ll-eyebrow">Как это выглядит для клиентки</span>
            <h2>Как клиентка находит именно вас</h2>
          </div>

          <div className="ll-steps-grid">
            {steps.map((step, index) => (
              <div className="ll-shot-card" key={index} data-landing-reveal>
                <p>{step.text}</p>
                <div className="ll-shot-card-frame">
                  <img src={step.image} alt={step.alt} loading="lazy" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ll-difference">
        <div className="ll-container">
          <span className="ll-eyebrow" data-landing-reveal>
            Как это меняет выбор клиентки
          </span>
          <h2 data-landing-reveal>Выбирают не самого дешёвого. Выбирают того, чья работа понравилась.</h2>

          <div className="ll-compare-grid" data-landing-reveal>
            <div className="ll-icon-card ll-compare-plain">
              <span className="ll-compare-tag">Как обычно</span>
              <p className="ll-compare-list">
                Маникюр — 2 000 ₽
                <br />
                Дизайн — от 300 ₽
                <br />
                Снятие — 400 ₽
              </p>
              <p className="ll-compare-note">Сначала цена. Работу представляют по описанию.</p>
            </div>
            <div className="ll-shot-card ll-compare-photo">
              <span className="ll-compare-tag ll-compare-tag-dark">Looksty</span>
              <p className="ll-compare-strong">Сначала — работа, ради которой хочется записаться. Цена — уже второй вопрос.</p>
              <img src={workImage6} alt="Реальная работа мастера в Looksty" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="ll-benefits" id="benefits">
        <div className="ll-container">
          <h2 data-landing-reveal>Что вы получите</h2>

          <div className="ll-benefits-layout">
            <div className="ll-benefit-photo" data-landing-reveal>
              <img src={workImage5} alt="Реальная работа мастера в Looksty" loading="lazy" />
            </div>

            <div className="ll-benefit-cards">
              {benefits.map(({ icon: Icon, title, text }) => (
                <div className="ll-icon-card" key={title} data-landing-reveal>
                  <span className="ll-icon-badge">
                    <Icon size={20} color="#fff" />
                  </span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ll-feed-section" id="feed">
        <div className="ll-container">
          <div className="ll-feed-card" data-landing-reveal>
            <span className="ll-eyebrow">Честно</span>
            <h2>Мастеров пока немного — посмотрите сами</h2>
            <p>Томск, несколько специализаций, кто-то давно, кто-то на этой неделе. Каждая работа пока на виду, а не потеряна среди тысяч других — самое время быть в их числе.</p>
            <a className="ll-btn" href={FEED_URL} target="_blank" rel="noopener noreferrer">
              Посмотреть ленту Looksty →
            </a>

            <div className="ll-feed-thumbs">
              {feedThumbnails.map((src, index) => (
                <img key={index} src={src} alt="" loading="lazy" />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ll-form-section" id="master-landing-form">
        <div className="ll-container">
          <div className="ll-form-card" data-landing-reveal>
            <div className="ll-form-copy">
              <span className="ll-eyebrow">Как начать</span>
              <h2>Давайте знакомиться!</h2>
              <p>Коротко: имя и телеграм или телефон. Дальше я напишу лично и помогу с профилем.</p>
            </div>

            <form className="ll-form" onSubmit={handleSubmit} noValidate>
              <label>
                <span>Имя</span>
                <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Как к вам обращаться" autoComplete="name" />
              </label>

              <label>
                <span>Telegram или телефон</span>
                <input value={contact} onChange={(event) => setContact(event.target.value)} placeholder="Как удобнее с вами связаться" autoComplete="tel" />
              </label>

              {formError && (
                <div className="ll-form-error" role="alert">
                  {formError}
                </div>
              )}

              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Отправляем..." : "Показать свои работы"}
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="ll-footer">
        <div className="ll-container ll-footer-row">
          <a href={FEED_URL} target="_blank" rel="noopener noreferrer">
            Посмотреть ленту
          </a>
          <a href="#top">Наверх</a>
        </div>
        <div className="ll-container ll-footer-logo-row">
          <span className="ll-logo">Looksty</span>
        </div>
      </footer>
    </main>
  );
};
